// ===========================================
// Instruments Management Page
// ===========================================
// CRUD interface for managing instruments with drag-and-drop reordering

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Typography,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PageHeader from '../../components/common/PageHeader';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ColorPickerField from '../../components/common/ColorPickerField';
import {
  useInstruments,
  useCreateInstrument,
  useUpdateInstrument,
  useDeleteInstrument,
  useReorderInstruments,
} from '../../hooks/useAdmin';
import { Instrument } from '../../api/admin.api';

// ===========================================
// SORTABLE ITEM COMPONENT
// ===========================================

interface SortableInstrumentItemProps {
  instrument: Instrument;
  onEdit: (instrument: Instrument) => void;
  onDelete: (instrument: Instrument) => void;
}

function SortableInstrumentItem({ instrument, onEdit, onDelete }: SortableInstrumentItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: instrument.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const canDelete = !instrument.isActive;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          mr: 2,
          color: 'text.secondary',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIcon />
      </Box>
      {instrument.color && (
        <Box
          sx={{
            width: 24,
            height: 24,
            bgcolor: instrument.color,
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            mr: 1.5,
            flexShrink: 0,
          }}
        />
      )}
      <ListItemText
        primary={instrument.name}
        secondary={`Order: ${instrument.sortOrder}`}
        sx={{ flex: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {instrument.isActive ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(instrument)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'Set status to Inactive to delete this instrument'}>
            <span>
              <IconButton
                size="small"
                onClick={() => canDelete && onDelete(instrument)}
                color={canDelete ? 'error' : 'default'}
                disabled={!canDelete}
                sx={{
                  opacity: canDelete ? 1 : 0.5,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </ListItem>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function InstrumentsPage() {
  const { data: instrumentsData, isLoading, error } = useInstruments();
  const createMutation = useCreateInstrument();
  const updateMutation = useUpdateInstrument();
  const deleteMutation = useDeleteInstrument();
  const reorderMutation = useReorderInstruments();

  // Get instruments array from response
  const instruments = instrumentsData ?? [];

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState<Instrument | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    isActive: true,
    color: null as string | null,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instrumentToDelete, setInstrumentToDelete] = useState<Instrument | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter instruments
  const filteredInstruments = useMemo((): Instrument[] => {
    if (hideInactive) {
      return instruments.filter((instrument: Instrument) => instrument.isActive);
    }
    return instruments;
  }, [instruments, hideInactive]);

  // Handlers
  const handleAdd = () => {
    setEditingInstrument(null);
    setFormData({ name: '', isActive: true, color: null });
    setModalOpen(true);
  };

  const handleEdit = (instrument: Instrument) => {
    setEditingInstrument(instrument);
    setFormData({
      name: instrument.name,
      isActive: instrument.isActive,
      color: instrument.color,
    });
    setModalOpen(true);
  };

  const handleDelete = (instrument: Instrument) => {
    setInstrumentToDelete(instrument);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingInstrument) {
        await updateMutation.mutateAsync({
          id: editingInstrument.id,
          data: {
            name: formData.name,
            isActive: formData.isActive,
            color: formData.color,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          color: formData.color ?? undefined,
        });
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (instrumentToDelete) {
      try {
        await deleteMutation.mutateAsync(instrumentToDelete.id);
        setDeleteDialogOpen(false);
        setInstrumentToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Reorder the full list, not just filtered
      const reorderedIds = arrayMove(
        instruments.map((i: Instrument) => i.id),
        instruments.findIndex((i: Instrument) => i.id === active.id),
        instruments.findIndex((i: Instrument) => i.id === over.id)
      );

      // Call the reorder API
      try {
        await reorderMutation.mutateAsync(reorderedIds);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Instruments"
        subtitle="Manage available instruments for lessons. Drag to reorder how instruments appear in dropdown menus."
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Instruments' },
        ]}
        actionLabel="Add Instrument"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load instruments. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive instruments"
        />

        {filteredInstruments.length !== instruments.length && (
          <Chip
            label={`Showing ${filteredInstruments.length} of ${instruments.length} instruments`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Instruments List */}
      <Paper sx={{ overflow: 'hidden' }}>
        {isLoading ? (
          <List>
            {[1, 2, 3, 4, 5].map((i) => (
              <ListItem key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ mr: 2 }}>
                  <Skeleton variant="rectangular" width={24} height={24} />
                </Box>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                  secondary={<Skeleton variant="text" width="30%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : filteredInstruments.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {instruments.length > 0
                ? 'No instruments match the current filter.'
                : "No instruments configured. Click 'Add Instrument' to create one."}
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredInstruments.map((i: Instrument) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {filteredInstruments.map((instrument: Instrument) => (
                  <SortableInstrumentItem
                    key={instrument.id}
                    instrument={instrument}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        )}
      </Paper>

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingInstrument ? 'Edit Instrument' : 'Add Instrument'}
        submitLabel={editingInstrument ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Instrument Name"
            placeholder="e.g., Piano"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
          <ColorPickerField
            label="Color"
            value={formData.color}
            onChange={(color) => setFormData({ ...formData, color })}
            helperText="Optional color for visual identification"
            useSchoolPalette
          />
          {editingInstrument && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Instrument"
        message={`Are you sure you want to delete "${instrumentToDelete?.name}"? Teachers assigned to this instrument will need to be reassigned.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
